/** @jsxImportSource @emotion/react */
'use memo'

import { DownOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'

interface Props {
  divisionCount: number
  isExpanded: boolean
  /** Id of the region this row shows and hides. */
  controlsId: string
  onToggle: () => void
}

/**
 * The full-width disclosure row under a card that shows and hides its
 * divisions table. A disclosure, not an action, so it is a row rather than
 * one of the shared action buttons. With no divisions there is nothing to
 * disclose: the row stays, as a plain line with its count, and drops the
 * Show Detail control.
 */
const OrgRegistrationDivisionsToggle = ({
  divisionCount,
  isExpanded,
  controlsId,
  onToggle,
}: Props) => {
  'use memo'

  const summary = (
    <span>
      {capitalizeWords('Divisions & Fees')}
      <Count>
        {' · '}
        {divisionCount} division{divisionCount === 1 ? '' : 's'}
      </Count>
    </span>
  )

  if (divisionCount === 0) {
    return <StaticRow>{summary}</StaticRow>
  }

  return (
    <Row
      type='button'
      aria-expanded={isExpanded}
      aria-controls={controlsId}
      onClick={onToggle}
    >
      {summary}
      <Hint>
        {capitalizeWords(isExpanded ? 'Hide detail' : 'Show detail')}
        <Chevron isExpanded={isExpanded} aria-hidden='true'>
          <DownOutlined />
        </Chevron>
      </Hint>
    </Row>
  )
}

export default OrgRegistrationDivisionsToggle

const rowBase = css`
  ${paragraphSmall}
  ${weightSemiBold}
  width: 100%;
  height: 48px;
  padding: 0 24px;
  /* Equal side tracks, so the Show / Hide hint sits on the card's centre line
     whatever the label's width. */
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  border: 0;
  border-top: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_350};
  color: ${colors.WHITE};
  text-align: left;
`

const StaticRow = styled.div`
  ${rowBase}
`

const Row = styled.button`
  ${rowBase}
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_300};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: -2px;
  }
`

const Count = styled.span`
  ${weightMedium}
  color: ${colors.NEUTRAL_50};
`

const Hint = styled.span`
  ${paragraphXSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.NEUTRAL_50};
`

const Chevron = styled.span<{ isExpanded: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  display: grid;
  place-items: center;
  color: ${colors.WHITE};
  transition: transform 0.2s ease;
  transform: rotate(${({ isExpanded }) => (isExpanded ? '180deg' : '0deg')});
`
